import { WIDGET_DEFAULT_FONT } from 'constants/fonts';

import { ColorPaletteName, Palette } from './MapWidget.types';

export const DEFAULT_MONOCHROME_BACKGROUND_COLOR = '#003F7F';
export const DEFAULT_BUBBLE_BACKGROUND_COLOR = '#BBD0F7';
export const DEFAULT_BUBBLE_COLOR = 'rgba(0, 63, 127, 0.5)'; // This is #003F7F with 50% opacity.

export const DEFAULT_HEIGHT = 400;
export const DEFAULT_WIDTH = 550;

export const DEFAULT_COLOR_STEPS = 5;

// For now we accept 3 colors to 9 colors
export const COLOR_STEPS_OPTIONS_OFFSET = 3;
export const COLOR_STEPS_OPTIONS = Array(7)
  .fill(0)
  .map((_, i) => {
    return {
      label: (i + COLOR_STEPS_OPTIONS_OFFSET).toString(),
      value: i.toString(),
    };
  });

const BLUE_COLOR_PALETTE: Array<Palette> = [
  null, // 0 color set unused
  null, // 1 color set unused
  null, // 2 color set unused
  ['#DFEEFF', '#5E78BE', '#00144B'], // 3 color set
  ['#DFEEFF', '#829DE9', '#3D5596', '#00144B'], // 4 color set
  ['#DFEEFF', '#97B1F1', '#5E78BE', '#2D4482', '#00144B'], // 5 color set
  ['#DFEEFF', '#A5BEF4', '#738ED7', '#4A63A6', '#243A77', '#00144B'], // 6 color set
  ['#DFEEFF', '#AFC6F6', '#829DE9', '#5E78BE', '#3D5596', '#1E346F', '#00144B'], // 7 color set
  ['#DFEEFF', '#B6CCF7', '#8CA9EF', '#6D88D0', '#5069AD', '#344B8B', '#192F6A', '#00144B'], // 8 color set
  ['#DFEEFF', '#BBD0F7', '#97B1F1', '#7994DE', '#5E78BE', '#455EA0', '#2D4482', '#162C66', '#00144B'], // 9 color set
];

const TEAL_COLOR_PALETTE: Array<Palette> = [
  null, // 0 color unused
  null, // 1 color unused
  null, // 2 colors unused
  ['#C9F4F9', '#0196A8', '#013540'], // 3 color set
  ['#C9F4F9', '#3EB8C7', '#007484', '#013540'], // 4 color set
  ['#C9F4F9', '#62C7D4', '#0196A8', '#015A68', '#013540'], // 5 color set
  ['#C9F4F9', '#77D1DC', '#1EAABC', '#028193', '#015A68', '#013540'], // 6 color set
  ['#C9F4F9', '#86D7E1', '#3EB8C7', '#0196A8', '#007484', '#025361', '#013540'], // 7 color set
  ['#C9F4F9', '#90DBE4', '#52C1CF', '#0FA4B7', '#038799', '#006A7A', '#024F5C', '#013540'], // 8 color set
  ['#C9F4F9', '#98DEE7', '#62C7D4', '#2AAFBF', '#0196A8', '#007C8D', '#016372', '#024B58', '#013540'], // 9 color set
];

const PURPLE_COLOR_PALETTE: Array<Palette> = [
  null, // 0 color unused
  null, // 1 color unused
  null, // 2 colors unused,
  ['#EBDBFF', '#8865EF', '#000092'], // 3 color set
  ['#EBDBFF', '#A98EF4', '#633FE1', '#000092'], // 4 color set
  ['#EBDBFF', '#BAA1F7', '#8865EF', '#4C30CF', '#000092'], // 5 color set
  ['#EBDBFF', '#C4ADF9', '#9D7EF2', '#744CEB', '#3F28C4', '#000092'], // 6 color set
  ['#EBDBFF', '#CAB5FA', '#A98EF4', '#8865EF', '#633FE1', '#3522BC', '#000092'], // 7 color set
  ['#EBDBFF', '#CEBAFA', '#A98EF4', '#8865EF', '#7A53EC', '#5537D6', '#2D1DB7', '#000092'], // 8 color set
  ['#EBDBFF', '#D3BFFB', '#BAA1F7', '#A284F3', '#8865EF', '#6E46E9', '#4C30CF', '#291AB3', '#000092'], // 9 color set
];

const RED_PURPLE_COLOR_PALETTE: Array<Palette> = [
  null, // 0 color unused
  null, // 1 color unused
  null, // 2 colors unused,
  ['#FDE0DD', '#FA9FB5', '#C51B8A'], // 3 color set
  ['#FEEBE2', '#FBB4B9', '#F768A1', '#AE017E'], // 4 color set
  ['#FEEBE2', '#FBB4B9', '#F768A1', '#C51B8A', '#7A0177'], // 5 color set
  ['#FEEBE2', '#FCC5C0', '#FA9FB5', '#F768A1', '#C51B8A', '#7A0177'], // 6 color set
  ['#FEEBE2', '#FCC5C0', '#FA9FB5', '#F768A1', '#DD3497', '#AE017E', '#7A0177'], // 7 color set
  ['#FFF7F3', '#FDE0DD', '#FCC5C0', '#FA9FB5', '#F768A1', '#DD3497', '#AE017E', '#7A0177'], // 8 color set
  ['#FFF7F3', '#FDE0DD', '#FCC5C0', '#FA9FB5', '#F768A1', '#DD3497', '#AE017E', '#7A0177', '#49006A'], // 9 color set
];

const YELLOW_RED_COLOR_PALETTE: Array<Palette> = [
  null, // 0 color unused
  null, // 1 color unused
  null, // 2 colors unused,
  ['#FFEDA0', '#FEB24C', '#F03B20'], // 3 color set
  ['#FFFFB2', '#FECC5C', '#FD8D3C', '#E31A1C'], // 4 color set
  ['#FFFFB2', '#FECC5C', '#FD8D3C', '#F03B20', '#BD0026'], // 5 color set
  ['#FFFFB2', '#FED976', '#FEB24C', '#FD8D3C', '#F03B20', '#BD0026'], // 6 color set
  ['#FFFFB2', '#FED976', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#B10026'], // 7 color set
  ['#FFFFCC', '#FFEDA0', '#FED976', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#B10026'], // 8 color set
  ['#FFFFCC', '#FFEDA0', '#FED976', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#BD0026', '#800026'], // 9 color set
];

const YELLOW_GREEN_COLOR_PALETTE: Array<Palette> = [
  null, // 0 color unused
  null, // 1 color unused
  null, // 2 colors unused,
  ['#D3F2A3', '#449A89', '#074050'], // 3 color set
  ['#D3F2A3', '#6DB893', '#237B7B', '#074050'], // 4 color set
  ['#D3F2A3', '#84C797', '#449A89', '#166C72', '#074050'], // 5 color set
  ['#D3F2A3', '#93D099', '#5CAC90', '#2F8782', '#10636C', '#074050'], // 6 color set
  ['#D3F2A3', '#9DD69B', '#6DB893', '#449A89', '#237B7B', '#0D5D68', '#074050'], // 7 color set
  ['#D3F2A3', '#A4DA9C', '#7AC196', '#55A78E', '#358D84', '#1B7276', '#0B5965', '#074050'], // 8 color set
  ['#D3F2A3', '#B0DB9B', '#8DC493', '#6CAD8B', '#499683', '#277F7B', '#19666B', '#10505A', '#074050'], // 9 color set
];

export const PRESET_PALETTE: Array<{ label: ColorPaletteName; colors: Palette[] }> = [
  {
    label: ColorPaletteName.BLUE,
    colors: BLUE_COLOR_PALETTE,
  },
  {
    label: ColorPaletteName.TEAL,
    colors: TEAL_COLOR_PALETTE,
  },
  {
    label: ColorPaletteName.PURPLE,
    colors: PURPLE_COLOR_PALETTE,
  },
  {
    label: ColorPaletteName.RED_PURPLE,
    colors: RED_PURPLE_COLOR_PALETTE,
  },
  {
    label: ColorPaletteName.YELLOW_RED,
    colors: YELLOW_RED_COLOR_PALETTE,
  },
  {
    label: ColorPaletteName.YELLOW_GREEN,
    colors: YELLOW_GREEN_COLOR_PALETTE,
  },
];

export const DEFAULT_FONT_FAMILY = WIDGET_DEFAULT_FONT;

export const DEFAULT_TEXT_STYLE = {
  color: '#000000',
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 'normal',
  textOutline: 'none',
  textDecoration: 'none',
};
